import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, PropsWithChildren } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import type { AuthConfig, SessionOutcome } from '@bettercomp/ams-spa-auth';
import { AuthContext, useAmsAuthSession, AuthContextValue } from '@bettercomp/ams-spa-auth-react';
import { UserService } from 'types';
import { amsPublicApiConfigGetAuthConfig } from 'service/amsApi';
import { hasDevIdentity } from 'service/amsDevAuth';
import { hasAmsSessionArtifact, setAmsSession } from 'service/amsSession';
import { setUserData } from 'redux/user';
import { selectUser } from 'redux/user/selectors';
import { TypedDispatch } from 'redux/configureStore';
import { reportError } from 'utils/reporting';
import {
  type HostOutcome,
  classifyIdentityLoadFailure,
  describeIdentityLoadFailure,
  isIdentityUnavailable,
} from 'components/AmsAuth/hostOutcomes';
import { usePageWorkspace } from 'components/AmsAuth/usePageWorkspace';

type AmsAdoptionContextValue = {
  /**
   * Arms the AMS session machinery for a fresh login: the login callback calls
   * this after the feature flag confirms the caller's company is on AMS auth,
   * making the provider fetch config, construct and bootstrap the session, and
   * load the user (with the sign-in audit) once the mint lands. Idempotent.
   */
  requestAdoption: () => void;
};

/** Outcome kind plus its source/code for error reports; never carries identity. */
const describeOutcome = (outcome: SessionOutcome): string =>
  outcome.kind === 'terminal' || outcome.kind === 'unavailable'
    ? `${outcome.kind} (${outcome.source}/${outcome.code})`
    : outcome.kind;

const AmsAdoptionContext = createContext<AmsAdoptionContextValue | undefined>(undefined);

export function useAmsAdoption(): AmsAdoptionContextValue {
  const context = useContext(AmsAdoptionContext);
  if (!context) {
    throw new Error('useAmsAdoption must be used within AmsAuthProvider');
  }
  return context;
}

/**
 * App-local owner of the AMS auth session. Stands fully down (no config fetch,
 * no session) unless this browser holds an AMS session artifact (amsAuth
 * cookie — reload of an AMS session) or the login callback requested adoption;
 * legacy-only environments therefore never touch /ams/*.
 *
 * The outcome handed to the gate is derived, not the session's as published:
 * a minted session opens the gate only once the app's own identity
 * (GET /user/current) is in Redux, and a failed identity load holds the gate
 * on a host outcome that timer renewals cannot overwrite. Once the page's
 * workspace moves (usePageWorkspace), the gate keeps its spinner until the
 * tab reloads.
 */
export function AmsAuthProvider({ children }: PropsWithChildren) {
  const dispatch = useDispatch<TypedDispatch>();
  const user = useSelector(selectUser);
  const [devBypass] = useState(hasDevIdentity);
  const [hasArtifact] = useState(hasAmsSessionArtifact);
  const [adoptionRequested, setAdoptionRequested] = useState(false);
  const [identityFailure, setIdentityFailure] = useState<HostOutcome | null>(null);

  const requestAdoption = useCallback(() => setAdoptionRequested(true), []);

  const enabled = (hasArtifact || adoptionRequested) && !devBypass;

  const configQuery = useQuery({
    queryKey: ['ams-auth-config'],
    queryFn: async () => {
      // Report both failure shapes with their cause — an HTTP error body and a
      // thrown network error — so on-call can tell an outage from a contract
      // break without reproducing locally; the UI only ever sees configError.
      try {
        const { data, error } = await amsPublicApiConfigGetAuthConfig();
        if (error) {
          throw new Error(`Failed to fetch the AMS auth config: ${JSON.stringify(error)}`);
        }
        return data;
      } catch (err) {
        reportError(err);
        throw err;
      }
    },
    staleTime: Infinity,
    retry: false,
    enabled,
  });

  const configError = enabled && Boolean(configQuery.error);

  const config = useMemo<AuthConfig | undefined>(
    () =>
      configQuery.data
        ? {
            hostedUiUrl: configQuery.data.hosted_ui_url,
            clientId: configQuery.data.client_id,
            exchangeUrl: configQuery.data.exchange_url,
          }
        : undefined,
    [configQuery.data],
  );

  // Unlike the AMS SPAs, no onRenewed callback and no signInRequired
  // subscription anywhere in app/: recovery happens at the transport layer
  // (fetchWithRenewal retries the failed request itself), which reaches every
  // client stack — most of app/ is not react-query, so the SPAs'
  // signal-then-invalidate model would miss the majority of callers.
  // bootstrapOnCallback: no CallbackView owns the exchange here — the login
  // callback only seeds the amsAuth cookie and arms adoption, so the session
  // must mint itself even while the URL still carries the Cognito code.
  const { session, outcome: sessionOutcome, reportOutcome, retryBootstrap } = useAmsAuthSession({
    config,
    enabled,
    bootstrapOnCallback: true,
    // Flag on means AMS or an error, so an adoption that does not mint has
    // stranded the login and is reported with kind and code (never the email)
    // so a flag-on that strands users is visible in Datadog before support
    // hears about it. A mint needs no callback: the identity effect below
    // reacts to the published outcome. Renewals and cross-tab sign-outs never
    // reach this callback.
    onBootstrapped: (bootstrapOutcome) => {
      if (bootstrapOutcome.kind !== 'minted' && adoptionRequested) {
        reportError(new Error(`AMS adoption failed: ${describeOutcome(bootstrapOutcome)}`));
      }
    },
  });

  useEffect(() => {
    setAmsSession(session);
    return () => setAmsSession(undefined);
  }, [session]);

  useEffect(() => {
    if (sessionOutcome?.kind === 'signedOut') {
      dispatch(setUserData(null));
    }
  }, [sessionOutcome, dispatch]);

  const sessionMinted = sessionOutcome?.kind === 'minted';

  // The identity phase. A login also fires the sign-in audit (login=true),
  // which the callback used to run with the legacy token. Only the api's
  // app-access refusal is a verdict about the account; every other failure is
  // the identity being unavailable, with retry (see hostOutcomes.ts).
  useEffect(() => {
    if (!sessionMinted || user || identityFailure) {
      return undefined;
    }
    // A result that lands after the session moved on (a cross-tab sign-out
    // while the request is in flight) belongs to no live session: dropped,
    // neither stored nor held as a failure.
    let cancelled = false;
    void UserService.getCurrentUser(adoptionRequested ? { login: true } : {}).then(
      (currentUser) => {
        if (!cancelled) {
          dispatch(setUserData(currentUser));
        }
      },
      (error: unknown) => {
        if (cancelled) {
          return;
        }
        const failure = classifyIdentityLoadFailure(error);
        reportError(new Error(
          `AMS post-mint identity load failed: ${describeIdentityLoadFailure(error)} -> ${describeOutcome(failure)}`,
        ));
        setIdentityFailure(failure);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [sessionMinted, user, identityFailure, adoptionRequested, dispatch]);

  // What the gate renders: a minted session counts as established only with
  // the user in Redux. Until then the gate keeps its spinner; a failed load
  // holds it on the host outcome. Anything but a mint passes through.
  const gateOutcome = sessionMinted ? identityFailure ?? (user ? sessionOutcome : undefined) : sessionOutcome;
  // The spinner unmounts the page, and its unsaved-changes guard with it, before the reload.
  const workspaceMoved = usePageWorkspace(sessionOutcome);
  const outcome = workspaceMoved ? undefined : gateOutcome;

  const value = useMemo<AuthContextValue>(
    () => ({
      outcome,
      configError,
      devBypass,
      session,
      reportOutcome,
      retry: () => {
        if (configError) {
          void configQuery.refetch();
          return;
        }
        if (isIdentityUnavailable(outcome)) {
          // The session is fine; only the identity load failed. Clearing the
          // failure re-runs the load, never the bootstrap.
          setIdentityFailure(null);
          return;
        }
        retryBootstrap();
      },
    }),
    [outcome, configError, devBypass, session, reportOutcome, retryBootstrap, configQuery],
  );

  const adoptionValue = useMemo<AmsAdoptionContextValue>(() => ({ requestAdoption }), [requestAdoption]);

  return (
    <AuthContext.Provider value={value}>
      <AmsAdoptionContext.Provider value={adoptionValue}>{children}</AmsAdoptionContext.Provider>
    </AuthContext.Provider>
  );
}
